import React from 'react';
import { Box, Paper } from '@mui/material';
import Typography from '../i18n/LocalizedTypography';
import { useTranslation } from 'react-i18next';
import KpDataTable from './KpDataTable';

const levels = ['level1', 'level2', 'level3', 'level4'] as const;
type PlanetRow = { planet: string; starLord: string; subLord: string; all: number[] } & Record<typeof levels[number], number[]>;

export default function KpClassicalSignificators({ view, planetRows, planetName, lordText, rotationHouse = 1 }: {
  view: 'houses' | 'planets'; planetRows: PlanetRow[]; planetName: (name: string) => string;
  lordText: (name: string) => string; rotationHouse?: number;
}) {
  const { t } = useTranslation();
  const title = t(view === 'houses' ? 'kp.houseSignificator' : 'kp.planetSignificator');
  const headers = [t(view === 'houses' ? 'kp.significatorHouseHeader' : 'kp.significatorPlanetHeader'),
    ...levels.map((_, index) => t('kp.significatorType', { type: String.fromCharCode(65 + index) }))];
  // Invert the actual A-D lists. houseSignificators[].level is a position
  // in the combined list, so it cannot be used as an A-D category.
  const rows = view === 'houses'
    ? Array.from({ length: 12 }, (_, index) => {
      const house = index + 1;
      const actualHouse = ((rotationHouse + house - 2) % 12) + 1;
      return { key: String(house), cells: [actualHouse === house ? String(house) : `${house} (${actualHouse})`,
        ...levels.map(level => planetRows.filter(row => row[level].includes(house)).map(row => planetName(row.planet)).join(', ') || '—')] };
    })
    : planetRows.map(row => ({ key: row.planet, cells: [planetName(row.planet), ...levels.map(level => row[level].join(', ') || '—')] }));
  return <Paper sx={{ p: 2 }}>
    <Typography variant="h6" sx={{ mb: 1.5 }}>{title}</Typography>
    <KpDataTable label={title} headers={headers} rows={rows} firstColumnWidth={view === 'houses' ? 80 : 110} />
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: view === 'planets' ? 2 : 0 }}>
      {levels.map((_, index) => <Typography key={index} variant="caption" color="text.secondary">
        {String.fromCharCode(65 + index)}: {t(`kp.${view === 'houses' ? 'fourHouseColumn' : 'fourLevelColumn'}${index + 1}`)}
      </Typography>)}
    </Box>
    {view === 'planets' && <>
      <KpDataTable label={t('kp.allSignificators')} headers={[t('kp.significatorPlanetHeader'), t('astrology.starLord'), t('astrology.subLord'), t('kp.allSignificators')]}
        rows={planetRows.map(row => ({ key: row.planet, cells: [planetName(row.planet), lordText(row.starLord), lordText(row.subLord), row.all.join(', ') || '—'] }))} />
      <Typography variant="caption" color="text.secondary">{t('kp.rahuKetuAgencyNote')}</Typography>
    </>}
  </Paper>;
}
